/**
 * salesConfigCache — the last sales-config the SERVER gave this device, per org.
 *
 * Born 2026-09-21 (owner report): a non-VAT business has "VAT Computation:
 * Disabled" in Settings, yet the sales screen switched to VAT whenever the
 * internet dropped. Cause: the screens START at `vat_enabled = true` and only
 * learn the real answer from GET /settings/sales-config, whose failure was
 * swallowed — offline, the optimistic default won. A default is not a fact:
 * what the server last said is. Screens start from this cache and refresh it
 * whenever the server answers.
 *
 * Keyed per organization — one device can sign in to more than one org (the
 * demo org has VAT on), and one org's answer must never leak into another.
 */

const PREFIX = 'agripos_sales_config:';
const keyFor = (orgId) => `${PREFIX}${orgId || 'unknown-org'}`;

export function readCachedSalesConfig(orgId) {
  try {
    const raw = window.localStorage.getItem(keyFor(orgId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && parsed.config && typeof parsed.config === 'object'
      ? parsed.config : null;
  } catch {
    return null;
  }
}

export function writeCachedSalesConfig(orgId, config) {
  if (!config || typeof config !== 'object') return;
  try {
    window.localStorage.setItem(keyFor(orgId), JSON.stringify({ config, saved_at: new Date().toISOString() }));
  } catch { /* storage full / blocked — the screen still works from the live answer */ }
}

/** VAT is on unless the server (now, or last time it answered) said otherwise. */
export function vatEnabledFrom(config) {
  return config?.vat_enabled !== false;
}

/**
 * Ask the server; remember the answer. When it cannot answer, fall back to
 * what it said last time. `null` = never heard from the server on this device.
 */
export async function loadSalesConfig(api, orgId) {
  try {
    const res = await api.get('/settings/sales-config');
    const config = res?.data && typeof res.data === 'object' ? res.data : {};
    writeCachedSalesConfig(orgId, config);
    return { config, source: 'server' };
  } catch {
    const cached = readCachedSalesConfig(orgId);
    return cached ? { config: cached, source: 'cache' } : null;
  }
}
